<template>
  <div class="d-flex h-full w-full flex-row col">
    <div class="col-md-3">
      <dx-tree-list
        class="flex-grow-1"
        ref="sprTreeListRef"
        key-expr="id"
        parent-id-expr="parent_id"
        display-expr="name"
        width="100%"
        height="100%"
        :root-value="null"
        :columns="[{ dataField: 'name', caption: 'Статьи затрат', width: '200px' }]"
        :data-source="sprDataSource"
        :filter-row="{ visible: true }"
        :show-row-lines="true"
        :show-borders="true"
        @selectionChanged="onSelectionChanged($event)"
      >
        <dx-selection mode="single" />
        <dx-load-panel :enabled="true" />
        <dx-scrolling mode="virtual" />
      </dx-tree-list>
    </div>
    <div class="d-flex flex-col h-full col-md-9">
      <dx-tree-list
        class="flex-grow-1"
        ref="treeListRef"
        width="100%"
        height="90%"
        key-expr="kod_direct"
        parent-id-expr="kod_parent"
        display-expr="name"
        :data-source="costDataSource"
        :columns="costPlanningViewColumns"
        :show-column-lines="true"
        :show-borders="true"
        :column-min-width="100"
        :word-wrap-enabled="false"
        :allow-column-reordering="false"
        :allow-column-resizing="true"
        :column-resizing-mode="'widget'"
        :column-chooser="{ enabled: true, mode: 'select' }"
        @content-ready="onContentMainTreeReady($event)"
        @toolbar-preparing="onToolbarPreparing($event)"
        @editor-preparing="onEditorPreparing($event)"
        @cell-prepared="onCellPrepared($event)"
      >
        <dx-load-panel :enabled="true" />
        <dx-selection mode="single" />
        <dx-scrolling mode="virtual" />
        <dx-editing
          :allow-updating="true"
          :allow-adding="false"
          :allow-deleting="false"
          start-edit-action="click"
          mode="batch"
        />
        <template #filterYearTemplate>
          <div class="mr-4">
            <span>Год:</span>
            <dx-select-box
              v-model="filter.year"
              :data-source="yearDataSource"
              width="120"
              :showClearButton="false"
              @valueChanged='reloadData()'
            />
          </div>
        </template>
        <template #filterFilialTemplate>
          <div class="mr-4">
            <span class="mr-1">Филиал/Управление предприятия:</span>
            <dx-select-box
              v-model="filter.filialId"
              :dataSource="filialRadio"
              value-expr="id"
              display-expr="name"
              width="auto"
              @value-changed="reloadData()"
            />
          </div>
        </template>
        <template #filterParentTemplate>
          <div class="mr-4 pt-3">
            <dx-check-box
              v-model="filter.inclParent"
              text="В т.ч. дочерние подразделения"
              @value-changed="reloadData()"
            />
          </div>
        </template>
        <template #filterEnergyTemplate>
          <div class="mr-4">
            <span class="mr-1">Вид энергии:</span>
            <dx-select-box
              v-model="filter.energyType"
              :dataSource="energyTypeRadio"
              value-expr="id"
              display-expr="name"
              @value-changed="reloadData()"
            />
          </div>
        </template>
        <template #filterRoundTemplate>
          <div v-if="!props.isPlan" class="mr-4 pt-3">
            <dx-check-box
              v-model="filter.prRound"
              text="С округлением до тыс.руб"
              @value-changed="reloadData()"
            />
          </div>
        </template>
      </dx-tree-list>
      <footer-grid
        class="pr-2"
        :totalCount="totalCount"
        :showSelectedCounter="false"
      ></footer-grid>
    </div>
  </div>
</template>

<script>
export default  {
  name: 'mped-cost-planning-view'
}
</script>

<script setup>
import { ref, onBeforeMount, defineProps, computed, watch } from 'vue';
import { useRouter } from "vue-router";
import {
  DxTreeList,
  DxSelectBox,
  DxCheckBox,
} from 'devextreme-vue';
import {
  DxEditing,
  DxLoadPanel,
  DxScrolling,
  DxSelection,
} from 'devextreme-vue/tree-list';

import FooterGrid from '@/components/grid-card-component/footer-grid.vue';
import { expandTree, getAiDataSource, onLoadedData } from '@/components/utils/ai-data-source-generator';
import {
  checkHasEditData,
  getAiToolbar,
  getGroupedEditingData,
  saveFullData,
  undoEditData,
} from '@/components/utils/ai-toolbar-generator';
import {
  costPlanningViewColumns,
  filialRadio,
  energyTypeRadio,
  getEstimateAllPlan,
  setEstimateAllPlan,
  getEstimateAllFact,
  setEstimateAllFact,
  getSprSmetCombo,
  getYearsList,
} from '@/views/mped/services/mped-estimate-service';
import { getUserData, setUserData } from '@/views/mped/services/mped-service';

const
  sprTreeListRef = ref(),
  treeListRef = ref(),
  yearDataSource = ref([]),
  sprDataSource = ref([]),
  summaryColumns = ref(),
  totalCount = ref(0),
  isExpand = ref(true),
  filter = ref({
    year: new Date().getFullYear(),
    filialId: '1',
    energyType: '0',
    kodSprSmetId: null,
    inclParent: 0,
    prRound: true
  });
let allSprIds = [];
let allIds = [];
let isAfterLoad = false;

const props = defineProps({
  isPlan: {
    type: Boolean
  }
});

const router = useRouter();

const costDataSource = getAiDataSource(
  {
    validationFn: () => filter.value.kodSprSmetId,
    dataFn: props.isPlan ? getEstimateAllPlan : getEstimateAllFact,
    paramsFn: () => Object.assign({}, { ...filter.value, plFactId: props.isPlan ? 0 : 1, formaId: 2 }),
    key: 'kod_direct',
    onLoadedFn: (result) => {
      totalCount.value = result.totalCount;
      isAfterLoad = true;
      allIds = result.data.map(x => x.kod_direct);
      onLoadedData({
        data: result.data,
        saveButton: toolbarButtons.save,
        undoEditData,
        dxInstance: getInstance()
      });
    },
  }
);

let toolbarButtons = {};

const userDataKey = computed({
  get: () => router.currentRoute.value.name
});

watch(filter.value, () => {
  saveUserData();
});

const saveUserData = () => {
  if (userDataKey.value) {
    setUserData(userDataKey.value, filter.value);
  }
};

onBeforeMount(async () => {
  if (userDataKey.value && getUserData(userDataKey.value))
    filter.value = getUserData(userDataKey.value);
  yearDataSource.value = getYearsList((new Date()).getFullYear(), 4, 4);
  sprDataSource.value = await getSprSmetCombo({ viewMode: '0', formaId: '-1', year: filter.value.year });
  allSprIds = sprDataSource.value.map(x => x.id);
  expandTree({
    ids: allSprIds,
    dxInstance: sprTreeListRef.value.instance
  });
  summaryColumns.value = costPlanningViewColumns.filter(c => c.summary);
});

const onContentMainTreeReady = (e) => {
  checkHasEditData(treeListRef.value.instance, toolbarButtons);
  if (isAfterLoad) {
    expandTree({
      ids: allIds,
      dxInstance: getInstance()
    });
  }
  isAfterLoad = false;
  if (filter.value.kodSprSmetId)
    sprTreeListRef.value.instance.selectRows(filter.value.kodSprSmetId, true);
};

const onToolbarPreparing = (e) => {
  e.toolbarOptions.elementAttr = {
    class: 'ai-toolbar-row-with-label',
  };
  toolbarButtons = getAiToolbar(e, {
    customs: [
      {
        location: 'before',
        template: 'filterYearTemplate',
      }, {
        location: 'before',
        template: 'filterFilialTemplate',
      }, {
        location: 'before',
        template: 'filterParentTemplate',
      }, {
        location: 'before',
        template: 'filterEnergyTemplate',
      }, {
        location: 'before',
        template: 'filterRoundTemplate',
      }],
    expand: {
      event: (e) => {
        isExpand.value = !isExpand.value;
        expandTree({
          ids: allIds,
          dxInstance: getInstance(),
          expand: isExpand.value
        });
      },
    },
    refresh: {
      event: (e) => {
        reloadDataAndClearSort();
      }
    },
    save: {
      event: saveData
    },
    undo: {
      event: (e) => {
        undoEditData(treeListRef.value.instance);
      }
    }
  });
}

const onSelectionChanged = (e) => {
  filter.value.kodSprSmetId = e.selectedRowKeys[0];
  reloadData();
}

const reloadData = () => {
  costDataSource.reload();
}

const reloadDataAndClearSort = () => {
  reloadData();
  costDataSource.value.instance.clearSorting();
}

const saveData = async () => {
  await saveFullData({
    saveButton: toolbarButtons.save,
    dxInstance: getInstance(),
    saveFn: props.isPlan ? setEstimateAllPlan : setEstimateAllFact,
    params: await getSaveParams(),
    reloadDataFn: reloadData
  });
}

const getSaveParams = async () => {
  const editedRows = await getGroupedEditingData(treeListRef.value.instance, { full: true, simple: true });
  editedRows.forEach((row) => {
    if (!row.kod_direct) {
      row.kod_direct = filter.value.departmentId;
    }
    row.tep_el = 0;
  });
  return {
    save: {
      params: { ...filter.value, formaId: 2, plFactId: props.isPlan ? 0 : 1, },
      rows: editedRows
    }
  }
}

const getInstance = () => {
  return treeListRef.value.instance;
}

const onCellPrepared = (e) => {
  const colId = e.row?.data?.kod_col;

  if (e.rowType === 'data' && [0, 1, 2, 3, 4, 5, 13].includes(colId)) {
    const rowClassNameMap = {
      0: 'text-primary',
      5: 'text-primary',
      4: 'text-danger',
      13: 'text-success',
    };
    e.cellElement.classList.add(rowClassNameMap[colId]);
  }
}

const onEditorPreparing = (e) => {
  if (e.parentType === 'dataRow') {
    if (filter.value.energyType === '2' || [0, 4, 13].includes(e.row.data.kod_col)) {
      e.editorOptions.readOnly = true;
    } else if (e.row.data.kod_col === 2 && filter.value.energyType === '1') {
      e.editorOptions.readOnly = true;
    } else if (e.row.data.kod_col === 3 && filter.value.energyType === '0') {
      e.editorOptions.readOnly = true;
    }
  }
}

</script>

<style lang="scss" scoped>
.dx-treelist .dx-treelist-table .dx-header-row > td {
  padding-bottom: 0;
}
</style>
